import React from 'react';
import { Box, Divider, List, ListItem, ListItemButton, ListItemDecorator } from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DownloadIcon from '@mui/icons-material/Download';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const MotionBox = motion.create(Box);

const ContextMenu = ({
  open,
  position,
  item,
  onOpen,
  onDownload,
  onRename,
  onMove,
  onDelete,
  onClose,
}) => {
  if (!open || !position || !item) return null;

  const isFolder = item.type === 'folder';

  const menuItems = [
    {
      id: 'open',
      label: isFolder ? 'Open folder' : 'Download',
      icon: isFolder ? <FolderOpenIcon fontSize="small" /> : <DownloadIcon fontSize="small" />,
      onClick: () => {
        if (isFolder) {
          onOpen(item);
        } else {
          onDownload(item.path, item.name);
        }
        onClose();
      },
    },
    { id: 'divider-1', type: 'divider' },
    {
      id: 'rename',
      label: 'Rename',
      icon: <DriveFileRenameOutlineIcon fontSize="small" />,
      onClick: () => {
        onRename(item);
        onClose();
      },
    },
    {
      id: 'move',
      label: 'Move to...',
      icon: <DriveFileMoveOutlinedIcon fontSize="small" />,
      onClick: () => {
        onMove(item);
        onClose();
      },
    },
    { id: 'divider-2', type: 'divider' },
    {
      id: 'delete',
      label: 'Delete',
      icon: <DeleteOutlineIcon fontSize="small" />,
      color: 'danger',
      onClick: () => {
        onDelete(item);
        onClose();
      },
    },
  ];

  // Calculate position to stay within viewport
  const getAdjustedPosition = () => {
    const menuWidth = 200;
    const menuHeight = 220;
    const padding = 8;

    let x = position.x;
    let y = position.y;

    // Adjust horizontal position
    if (x + menuWidth > window.innerWidth - padding) {
      x = window.innerWidth - menuWidth - padding;
    }

    // Adjust vertical position
    if (y + menuHeight > window.innerHeight - padding) {
      y = window.innerHeight - menuHeight - padding;
    }

    return { x, y };
  };

  const adjustedPosition = getAdjustedPosition();

  return (
    <AnimatePresence>
      {open && (
        <MotionBox
          initial={{ opacity: 0, scale: 0.95, y: -8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -8 }}
          transition={{ duration: 0.12 }}
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'fixed',
            top: adjustedPosition.y,
            left: adjustedPosition.x,
            zIndex: 10000,
            minWidth: 180,
            bgcolor: 'background.surface',
            borderRadius: 'md',
            boxShadow: 'lg',
            border: '1px solid',
            borderColor: 'neutral.200',
            overflow: 'hidden',
          }}
        >
          <List
            size="sm"
            sx={{
              py: 0.5,
              '--ListItem-paddingY': '8px',
              '--ListItem-paddingX': '12px',
              '--ListItemDecorator-size': '28px',
            }}
          >
            {menuItems.map((menuItem) => {
              if (menuItem.type === 'divider') {
                return (
                  <Divider
                    key={menuItem.id}
                    sx={{ my: 0.5 }}
                  />
                );
              }

              return (
                <ListItem key={menuItem.id}>
                  <ListItemButton
                    onClick={menuItem.onClick}
                    color={menuItem.color || 'neutral'}
                    sx={{
                      borderRadius: 'sm',
                      '&:hover': {
                        bgcolor: menuItem.color === 'danger'
                          ? 'danger.50'
                          : 'background.level1',
                      },
                    }}
                  >
                    <ListItemDecorator
                      sx={{
                        color: menuItem.color === 'danger'
                          ? 'danger.500'
                          : 'neutral.500',
                      }}
                    >
                      {menuItem.icon}
                    </ListItemDecorator>
                    {menuItem.label}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </MotionBox>
      )}
    </AnimatePresence>
  );
};

export default ContextMenu;
